// Chat Subscription Service - Real-time Updates with Amplify v6
import { inject, Injectable, OnDestroy } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { from, Subject } from 'rxjs';
import { Channel, ChannelRead, ChatMessage } from '../models/chat.models';
import { AddChannel, UpdateChannel, LoadChannelStats, LoadGlobalChannelStats } from '../store/actions/channel.actions';
import { UpdateChannelRead, MarkChannelAsRead } from '../store/actions/channel-read.actions';
import { AddMessage } from '../store/actions/message.actions';
import {
    ON_CHANNEL_READ,
    ON_NEW_CHANNEL,
    ON_NEW_MESSAGE,
    ON_UNREAD_COUNT_CHANGE,
    ON_UPDATE_CHANNEL,
} from '../constants/graphql';
import { Howl } from 'howler';
import { ChannelState } from '../store/states/channel.state';
import { ChannelReadState } from '../store/states/channel-read.state';
import { CurrentUserModel } from '@shared/common/models';
import { getAllowedChannelTypes, SERVICE_CATEGORIES, ServiceCategory } from '../utils/chat-permissions.utils';

@Injectable({
    providedIn: 'root',
})
export class ChatSubscriptionService implements OnDestroy {
    private destroy$ = new Subject<void>();
    private currentUserId: string | null = null;
    private client = generateClient();
    private subscriptions: any[] = [];
    private allowedTypesSet = new Set<string>();
    private allowedTypesArray: string[] = [];
    private currentUser = inject(CurrentUserModel);
    private soundNewMessage = new Howl({
        src: ['/assets/sounds/new-message.mp3'],
    });

    private soundMessageSent = new Howl({
        src: ['/assets/sounds/message-sent.mp3'],
    });
    constructor(
        private store: Store,
        private router: Router,
    ) {}

    setAllowedTypes(categories: ServiceCategory[] = SERVICE_CATEGORIES): void {
        const types = getAllowedChannelTypes(this.currentUser.permissions, categories);
        this.allowedTypesSet = new Set(types);
        this.allowedTypesArray = types;
    }

    private isPermittedChannelType(type: string): boolean {
        return this.allowedTypesSet.size === 0 || this.allowedTypesSet.has(type);
    }

    async startSubscriptions(userId: string): Promise<void> {
        if (this.currentUserId === userId) {
            return;
        }

        this.stopSubscriptions();
        this.currentUserId = userId;

        try {
            this.subscribeToNewChannel();
            this.subscribeToChannelRead(userId);
            this.subscribeToNewMessage(userId);
            this.subscribeToUpdateChannel();
            this.subscribeToUnreadCountChange(userId);
        } catch (error) {
            console.error('❌ Error starting subscriptions:', error);
        }
    }

    private subscribeToNewChannel(): void {
        // Subscribe to new channels - Correct Amplify v6 pattern
        const newChannelObservable = this.client.graphql({
            query: ON_NEW_CHANNEL,
        });

        const newChannelSub = from(newChannelObservable).subscribe({
            next: (result: any) => {
                const newChannel: Channel = result.data?.onNewChannel;
                if (!newChannel || !this.isPermittedChannelType(newChannel.type)) return;
                console.log(`🆕 New channel received: ${newChannel.name}`);
                this.store.dispatch(new AddChannel(newChannel));
                this.store.dispatch(new LoadChannelStats(undefined, this.allowedTypesArray));
                this.store.dispatch(new LoadGlobalChannelStats(this.allowedTypesArray));
                this.soundNewMessage.stop().play();
            },
            error: (error: any) => console.error('❌ New channel subscription error:', error),
        });
        this.subscriptions.push(newChannelSub);
    }

    private subscribeToChannelRead(userId: string): void {
        const channelReadObservable = this.client.graphql({
            query: ON_CHANNEL_READ,
        });

        const channelReadSub = from(channelReadObservable).subscribe({
            next: (result: any) => {
                const channelRead: ChannelRead = result.data?.onChannelRead;
                if (channelRead && channelRead.userId === userId) {
                    this.store.dispatch(new UpdateChannelRead(channelRead));
                }
            },
            error: (error: any) => console.error('❌ Channel read subscription error:', error),
        });
        this.subscriptions.push(channelReadSub);
    }

    private subscribeToNewMessage(userId: string): void {
        const newMessageObservable = this.client.graphql({
            query: ON_NEW_MESSAGE,
        });

        const newMessageSub = from(newMessageObservable).subscribe({
            next: (result: any) => {
                const newMessage: ChatMessage = result.data?.onNewMessage;
                if (!newMessage) return;

                // Add the message to the store and update lastSenderId on the
                // channel so wait-time starts immediately without waiting for
                // the backend updateChannel round-trip.
                this.store.dispatch(new AddMessage(newMessage.channelId, newMessage));
                this.store.dispatch(
                    new UpdateChannel({
                        _id: newMessage.channelId,
                        lastSenderId: newMessage.senderId,
                        updatedAt: new Date().toISOString(),
                    } as Channel),
                );

                const selectedChannelId = this.store.selectSnapshot(
                    ChannelState.getSelectedChannelId,
                );
                const isViewingThisChannel =
                    selectedChannelId === newMessage.channelId &&
                    this.router.url === `/chat/${newMessage.channelId}`;

                if (newMessage.senderId !== userId) {
                    if (isViewingThisChannel) {
                        this.store.dispatch(
                            new MarkChannelAsRead(newMessage.channelId, userId, newMessage._id),
                        );
                    } else {
                        // Optimistically increment local unread count so the badge
                        // appears immediately. Preserve the server's last updatedAt
                        // so the authoritative onUnreadCountChange event (which
                        // carries a newer server timestamp) can overwrite this.
                        const getChannelRead = this.store.selectSnapshot(ChannelReadState.getChannelRead);
                        const currentRead = getChannelRead(newMessage.channelId, userId);
                        const currentCount = currentRead ? currentRead.unreadCount : 0;
                        this.store.dispatch(
                            new UpdateChannelRead({
                                channelId: newMessage.channelId,
                                userId,
                                unreadCount: currentCount + 1,
                                lastReadMessageId: currentRead?.lastReadMessageId ?? '',
                                updatedAt: currentRead?.updatedAt ?? new Date().toISOString(),
                            }),
                        );
                    }

                    const channels = this.store.selectSnapshot((state) => state.channels.channels);
                    const channel = channels.find((c: Channel) => c._id === newMessage.channelId);
                    if (channel && channel.members.includes(userId)) {
                        this.soundNewMessage.stop().play();
                    }
                } else this.soundMessageSent.stop().play();
            },
            error: (error: any) => console.error('❌ New message subscription error:', error),
        });
        this.subscriptions.push(newMessageSub);
    }

    private subscribeToUnreadCountChange(userId: string): void {
        const unreadCountObservable = this.client.graphql({
            query: ON_UNREAD_COUNT_CHANGE,
        });

        const unreadCountSub = from(unreadCountObservable).subscribe({
            next: (result: any) => {
                const channelRead: ChannelRead = result.data?.onUnreadCountChange;
                if (!channelRead || channelRead.userId !== userId) return;

                // Drop increment events for channels the user is actively reading;
                // the optimistic MarkChannelAsRead already cleared the badge.
                if (channelRead.unreadCount > 0 && this.isViewingChannel(channelRead.channelId)) {
                    return;
                }

                // Drop stale events — if the incoming event's timestamp is older
                // than (or equal to) what is already in state, discard it.
                // This prevents delayed subscription increments (triggered by messages
                // that arrived BEFORE the user read the channel) from re-inflating
                // the badge after MarkChannelAsRead has already cleared it.
                // The optimistic patch in MarkChannelAsRead uses a fresh client
                // timestamp, so any pre-read increment will always be older.
                const currentRead = this.store.selectSnapshot(ChannelReadState.getChannelRead)(
                    channelRead.channelId,
                    userId,
                );
                if (
                    currentRead?.updatedAt &&
                    channelRead.updatedAt &&
                    channelRead.updatedAt <= currentRead.updatedAt
                ) {
                    return;
                }

                this.store.dispatch(new UpdateChannelRead(channelRead));
            },
            error: (error: any) => console.error('❌ Unread count subscription error:', error),
        });
        this.subscriptions.push(unreadCountSub);
    }

    private subscribeToUpdateChannel(): void {
        const updateChannelObservable = this.client.graphql({
            query: ON_UPDATE_CHANNEL,
        });

        const updateChannelSub = from(updateChannelObservable).subscribe({
            next: (result: any) => {
                const updatedChannel: Channel = result.data?.onUpdateChannel;
                if (!updatedChannel || !this.isPermittedChannelType(updatedChannel.type)) return;
                console.log(`📝 Channel updated: ${updatedChannel.name}`);
                this.store.dispatch(new UpdateChannel(updatedChannel));
                this.store.dispatch(new LoadChannelStats(undefined, this.allowedTypesArray));
                this.store.dispatch(new LoadGlobalChannelStats(this.allowedTypesArray));
            },
            error: (error: any) => console.error('❌ Update channel subscription error:', error),
        });
        this.subscriptions.push(updateChannelSub);
    }

    private isViewingChannel(channelId: string): boolean {
        const selectedChannelId = this.store.selectSnapshot(ChannelState.getSelectedChannelId);
        return selectedChannelId === channelId && this.router.url === `/chat/${channelId}`;
    }

    stopSubscriptions(): void {
        if (this.currentUserId) {
            console.log(`🔌 Stopping Amplify chat subscriptions for user: ${this.currentUserId}`);
            this.currentUserId = null;
        }

        // Unsubscribe from all subscriptions
        this.subscriptions.forEach((sub) => {
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
        });
        this.subscriptions = [];

        this.destroy$.next();
        this.destroy$.complete();
        this.destroy$ = new Subject<void>();
    }

    ngOnDestroy(): void {
        this.stopSubscriptions();
    }
}
